import { memo, useCallback, useMemo, useState } from "react";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { fetchTokenTXS, fetchTokenMarket } from "~/api/endpoints";
import { useSnackbar } from "~/Provider/Snackbar";
import { Skeleton } from "antd-mobile";
import useSWR from "swr";
import { TradeToken } from "~/types";
import SimpleSegmentedControl from "~/pages/Market/SimpleSegmentedControl/SimpleSegmentedControl";
import clsx from "clsx";
import {
    IconTradeTXSBuy,
    IconTradeTXSSell,
    IconTradeTXSSwap,
} from "~/components/Iconfont/Iconfont";
import { formatMarketCap, formatNumber } from "~/utils/number-format";

interface IProps {
    children?: ReactNode;
    token: TradeToken;
    loading: boolean;
}

enum TOKEN_TXS_TYPE {
    BUY = 1,
    SELL = 2,
    SWAP = 3,
}

const TOKEN_TXS_OPTIONS = [
    {
        label: <IconTradeTXSSwap />,
        value: TOKEN_TXS_TYPE.SWAP,
    },
    {
        label: <IconTradeTXSBuy />,
        value: TOKEN_TXS_TYPE.BUY,
    },
    {
        label: <IconTradeTXSSell />,
        value: TOKEN_TXS_TYPE.SELL,
    },
];

const TradeAmountValueItem: React.FC<{
    item: { type: TOKEN_TXS_TYPE; amount: string; value: string };
    maxValue: { type: TOKEN_TXS_TYPE; amount: string; value: string };
}> = ({ item, maxValue }) => {
    return (
        <div className="w-full flex justify-between items-center h-[20px] relative">
            <span
                className={clsx(
                    "text-direction2 text-center transition-colors",
                    item.type === TOKEN_TXS_TYPE.BUY
                        ? "text-text_buy"
                        : "text-text_sell"
                )}
            >
                {item.type === TOKEN_TXS_TYPE.BUY ? "+" : "-"}
                {formatNumber(item.amount)}
            </span>
            <span className=" text-direction2 text-center text-text_1">
                {formatNumber(item.value)}
            </span>
            <div
                className="absolute left-0 h-5 top-0 transition-all"
                style={{
                    width: `${(Number(item.value) / Number(maxValue.value)) * 100}%`,
                    backgroundColor:
                        item.type === TOKEN_TXS_TYPE.BUY
                            ? "rgba(49, 209, 88, 0.1)"
                            : "rgba(254, 59, 48, 0.1)",
                }}
            />
        </div>
    );
};

const TradeBodyAmountList: FC<IProps> = ({ loading, token }) => {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const [type, setType] = useState(TOKEN_TXS_TYPE.SWAP);

    const chainId = token?.token_base?.chain_id;
    const contract = token?.token_base?.contract;

    const initTokenData = (args: any) => {
        const [_, chain_id, contract] = args;
        if (!chainId || !contract) return;
        return fetchTokenMarket(chain_id, contract).then(
            (res) => res?.data || {}
        );
    };

    const { data: tokenInfo } = useSWR(
        ["TradePage:fetchTokenMarket", chainId, contract],
        initTokenData,
        { fallbackData: {}, refreshInterval: 8000 }
    );

    const queryPriceImpact = useCallback(
        async ([_, chainId, contract, type]: any) => {
            if (!chainId || !contract) return;

            return fetchTokenTXS(String(chainId), contract, type).then(
                (res) => res.data
            );
        },
        []
    );

    const { data, isLoading } = useSWR(
        ["TradePage:queryTokenTXS", chainId, contract, type],
        queryPriceImpact,
        {
            refreshInterval: 2000,
            onError() {
                snackbar.error(
                    `Can not load token info! Check your network connection!`
                );
            },
        }
    );

    const dataset = useMemo(() => data?.list ?? [], [data]);
    const maxValue = useMemo(() => {
        return [...dataset].sort((a, b) =>
            Number(a.value) > Number(b.value) ? -1 : 1
        )[0];
    }, [dataset]);

    return (
        <div className="w-full h-full flex flex-col justify-between">
            <div className="h-4 text-text_2 text-direction3 font-semibold flex justify-between items-center">
                <span>{t("amount")}</span>
                <span>${t("market-detail-list-col3")}</span>
            </div>
            {loading || isLoading ? (
                <div className="h-[200px] overflow-hidden">
                    <Skeleton.Paragraph lineCount={10} animated />
                </div>
            ) : (
                <>
                    <div className="h-[200px]">
                        {data?.list
                            .slice(0, 10)
                            .map((item, index) => (
                                <TradeAmountValueItem
                                    key={index}
                                    item={item}
                                    maxValue={maxValue}
                                />
                            ))}
                    </div>
                </>
            )}
            <SimpleSegmentedControl
                className="w-full"
                size="small"
                options={TOKEN_TXS_OPTIONS}
                selectedValue={type}
                onSelectedValueChange={(type) => {
                    setType(type as number);
                }}
            />
            <div className="h-[44px] border-[0.5px] border-border_2 rounded p-1.5 flex flex-col justify-between">
                <div className="flex justify-between items-center h-3.5">
                    <span className="text-text_2 text-direction3">
                        {t("mkt_cap")}
                    </span>
                    <span className="text-text_1 text-direction3 font-semibold">
                        {formatMarketCap(tokenInfo?.token_market?.market_cap)}
                    </span>
                </div>
                <div className="flex justify-between items-center">
                    <span className="text-text_2 text-direction3">
                        {t("tvl")}
                    </span>
                    <span className="text-text_1 text-direction3 font-semibold">
                        {formatMarketCap(tokenInfo?.token_market?.tvl_usd)}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default memo(TradeBodyAmountList);
